import { useEffect, useState } from 'react';

import { useLocation, useNavigate } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { LocalStorageKeys } from '~/interfaces/local_storage';
import type { SignupFlow, SignupFlowStep } from '~/interfaces/signup_flow';
import { is_iOS } from '~/utils/deviceTest';
import { substitutePostSlugInUrl } from '~/utils/substitutePostSlugInUrl';

const SIGNUP_FLOW_KEY = 'signup_flow';

type Redirects = {
  id: string;
  startedAt: string;
  steps: SignupFlowStep[];
  currentIndex: number;
  nextIndex: number;
  openExternalLinkInSameTab: boolean;
  redirectBackToStart?: boolean;
  startUrl?: string;
};

interface ReturnProps {
  currentStep: any;
  nextStep: any;
  isInProgress: boolean;
  steps: any[];
  progress: number;
  startRedirects: (signupFlow: SignupFlow) => void;
  nextPage: () => void;
}

const useSignupRedirects = (): ReturnProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOpenModal } = useGlobalModals();
  const [redirects, setRedirects] = useState<Redirects>();
  const redirectLength = redirects?.steps?.length ?? 0;
  const stepCount = redirects ? redirects?.currentIndex + 1 : 0;
  const progress = (stepCount / redirectLength) * 100;
  const nextStep = redirects?.steps?.[redirects?.nextIndex];
  const isLastStep = redirects?.nextIndex === (redirects?.steps?.length ?? 0);

  const clearRedirects = () => {
    localStorage.removeItem(SIGNUP_FLOW_KEY);
  };

  const addCurrentSearchParamsToUrl = (
    url: URL,
    { overwriteExisting = false }: { overwriteExisting?: boolean } = {},
  ): URL => {
    if (typeof window === 'undefined') return url;

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (key === 'modal') return;

      if (overwriteExisting || !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    return url;
  };

  const handleExternalRedirect = (url: string, openExternalLinkInSameTab?: boolean) => {
    setTimeout(() => {
      // ios devices don't like window.open, so we redirect them directly
      if (is_iOS(navigator.userAgent)) {
        try {
          window.location.href = url;
        } catch {
          window.location.href = url;
        }
      } else {
        if (openExternalLinkInSameTab ?? redirects?.openExternalLinkInSameTab) {
          window.location.href = url;
        } else {
          window.open(url, '_blank');
          navigate('/', { preventScrollReset: true });
        }
      }
    }, 1000);
  };

  const handleStartRedirects = (signupFlow: SignupFlow) => {
    try {
      const processedSteps = signupFlow.steps
        .map((step) => {
          const processedUrl = substitutePostSlugInUrl(step.url, location.pathname);
          return processedUrl ? { ...step, url: processedUrl } : null;
        })
        .filter((step): step is SignupFlowStep => step !== null);

      if (processedSteps.length === 0) {
        navigate('/', { preventScrollReset: true });
        return;
      }

      const startUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';

      let finalSteps = processedSteps;
      const shouldRedirectToStart = signupFlow?.redirect_back_to_start === true;
      if (shouldRedirectToStart && startUrl) {
        try {
          const fullStartUrl = typeof window !== 'undefined' ? window.location.origin + startUrl : startUrl;
          finalSteps = [
            ...processedSteps,
            {
              url: fullStartUrl,
              external: false,
              modal_param: null,
            },
          ];
        } catch (error) {
          console.error('Error adding redirect back to start step:', error);
          finalSteps = processedSteps;
        }
      }

      localStorage.setItem(
        SIGNUP_FLOW_KEY,
        JSON.stringify({
          id: signupFlow.id,
          startedAt: new Date().toISOString(),
          steps: finalSteps,
          currentIndex: 0,
          nextIndex: 1,
          openExternalLinkInSameTab: signupFlow.open_external_link_in_same_tab ?? false,
          redirectBackToStart: signupFlow?.redirect_back_to_start ?? false,
          startUrl: startUrl,
        }),
      );

      try {
        const stringifiedEmail = localStorage.getItem(LocalStorageKeys.EMAIL);
        const email = stringifiedEmail && JSON.parse(stringifiedEmail);
        const processedFirstStep = processedSteps[0];
        const url = new URL(processedFirstStep.url);
        if (email) {
          url.searchParams.set('email', email);
        }
        addCurrentSearchParamsToUrl(url);

        if (processedFirstStep.external) {
          handleExternalRedirect(url.href, signupFlow.open_external_link_in_same_tab);
        } else {
          // Open modal on the same page
          if (url.pathname === '/' && url.search.includes('modal')) {
            navigate(window.location.pathname + url.search, { preventScrollReset: true });
            return;
          }

          const pathname = url.pathname + url.search;
          navigate(pathname, { preventScrollReset: true });
        }
      } catch (error) {
        console.error('Error navigating to first step:', error);
        navigate('/', { preventScrollReset: true });
      }
    } catch (error) {
      console.error('Error starting signup flow redirects:', error);
      navigate('/', { preventScrollReset: true });
    }
  };

  useEffect(() => {
    try {
      const redirectsInProcess = localStorage?.getItem(SIGNUP_FLOW_KEY);

      if (redirectsInProcess) {
        try {
          const parsedRedirects = JSON.parse(redirectsInProcess);
          const startedAt = parsedRedirects.startedAt;
          const startedAtTime = new Date(startedAt).getTime();
          const now = new Date().getTime();
          const timeSinceStarted = now - startedAtTime;
          const timeLimit = 1000 * 60 * 3;

          if (timeSinceStarted > timeLimit) {
            clearRedirects();
          } else {
            setRedirects(parsedRedirects);
          }
        } catch (error) {
          console.error('Error parsing signup flow redirects:', error);
          clearRedirects();
        }
      } else {
        redirects && setRedirects(undefined);
      }
    } catch (error) {
      console.error('Error in signup flow effect:', error);
    }
  }, [location]);

  useEffect(() => {
    try {
      if (redirects) {
        const currentStep = redirects.steps?.[redirects.currentIndex];
        if (!currentStep) return;

        try {
          const currentStepPathname = new URL(currentStep.url).pathname;
          const locationSearchParams = new URLSearchParams(location.search);
          const isOnWrongPage = currentStep.modal_param
            ? currentStep.modal_param !== locationSearchParams.get('modal')
            : !currentStepPathname.includes(location.pathname) ||
              (location.pathname === '/' && currentStepPathname !== location.pathname);

          if (isOnWrongPage) {
            clearRedirects();
            setRedirects(undefined);
          }
        } catch (error) {
          console.error('Error checking current step:', error);
          clearRedirects();
          setRedirects(undefined);
        }
      }
    } catch (error) {
      console.error('Error in signup flow navigation check:', error);
    }
  }, [redirects]);

  // If already on the last step, clear the redirects
  useEffect(() => {
    if (redirects?.currentIndex === undefined || redirects?.steps?.length === undefined) return;

    if ((redirects?.currentIndex || 0) >= (redirects?.steps?.length || 0) - 1) {
      clearRedirects();
    }
  }, [redirects?.currentIndex, redirects?.steps?.length]);

  return {
    currentStep: redirects?.steps?.[redirects?.currentIndex],
    nextStep,
    isInProgress: Boolean(redirects) && !isLastStep,
    steps: redirects?.steps || [],
    progress: Number(progress.toFixed(1)),
    startRedirects: (signupFlow: SignupFlow) => handleStartRedirects(signupFlow),
    nextPage: () => {
      if (!redirects) return;

      const lastIndex = redirects?.steps?.length - 1;

      if (redirects?.nextIndex > lastIndex) {
        clearRedirects();
        navigate('/', { preventScrollReset: true });
      } else {
        const stringifiedEmail = localStorage.getItem(LocalStorageKeys.EMAIL);
        const email = stringifiedEmail && JSON.parse(stringifiedEmail);
        const nextRedirect = redirects?.steps?.[redirects?.nextIndex];
        const isNextRedirectExternal = false || nextRedirect?.external;

        if (isNextRedirectExternal) {
          try {
            // Apply slug substitution to external redirect URL as well
            const processedUrl = substitutePostSlugInUrl(nextRedirect.url, location.pathname) || nextRedirect.url;
            const url = new URL(processedUrl);
            if (email) {
              url.searchParams.set('email', email);
            }
            addCurrentSearchParamsToUrl(url);
            handleExternalRedirect(url.href);
            clearRedirects();

            if (!redirects?.openExternalLinkInSameTab) {
              navigate('/', { preventScrollReset: true });
            }
          } catch {
            clearRedirects();
            navigate('/', { preventScrollReset: true });
          }
        } else {
          try {
            // Apply slug substitution to internal redirect URL
            const processedUrl = substitutePostSlugInUrl(nextRedirect.url, location.pathname) || nextRedirect.url;
            const nextUrl = new URL(processedUrl);
            if (email) {
              nextUrl.searchParams.set('email', email);
            }
            addCurrentSearchParamsToUrl(nextUrl);
            if (isLastStep) {
              clearRedirects();
            } else {
              const redirectState = {
                currentIndex: redirects?.currentIndex + 1,
                nextIndex: redirects?.nextIndex + 1,
                steps: redirects?.steps,
                openExternalLinkInSameTab: redirects?.openExternalLinkInSameTab,
                redirectBackToStart: redirects?.redirectBackToStart ?? false,
                startUrl: redirects?.startUrl ?? '/',
              };

              localStorage.setItem(SIGNUP_FLOW_KEY, JSON.stringify(redirectState));
            }

            if (nextRedirect?.modal_param) {
              handleOpenModal(nextRedirect.modal_param);
            } else {
              navigate(nextUrl.pathname + nextUrl.search, { preventScrollReset: true });
            }
          } catch {
            clearRedirects();
            navigate('/', { preventScrollReset: true });
          }
        }
      }
    },
  };
};

export default useSignupRedirects;
